import React, { useState } from 'react';
import { User, Mail, Phone, MessageSquare, AlertCircle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { ShareButtons } from './ShareButtons';

export const JoinSection = ({ onSubmit }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', note: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError(t('joinErrorName'));
      return;
    }
    if (!formData.email.trim()) {
      setError(t('joinErrorEmail'));
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      setError(t('joinErrorValidEmail'));
      return;
    }
    if (!formData.phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', note: '' });
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (err) {
      setError(err.message || t('joinErrorFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setFormData({ name: '', email: '', phone: '', note: '' });
    setError('');
  };

  return (
    <section id="waitlist" style={{ scrollMarginTop: '80px' }} className="py-16">
      <div className="mb-8">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t('joinGuardians')}</p>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-white">{t('joinHeading')}</h2>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr]">
        {/* Left Content */}
        <div className="space-y-6">
          <div>
            <h3 className="text-base font-semibold text-white mb-3">
              {t('joinStandWithUs')}
            </h3>
            <div className="text-sm text-slate-400 leading-relaxed space-y-2">
              <p>{t('joinText1')}</p>
              <p>{t('joinText2')}</p>
              <p>{t('joinText3')}</p>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            {t('joinDetailsStay')}
          </p>

          <ShareButtons />
        </div>

        {/* Right Form */}
        <div className="rounded-2xl p-6 bg-slate-900/40 backdrop-blur-sm border border-slate-700/50 shadow-xl">
          <div className="mb-4 text-xs font-semibold uppercase tracking-wide text-cyan-400">
            {t('joinPledgeLabel')}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="sr-only">Name</label>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700/50 focus-within:border-cyan-400/50 transition-colors">
                <User className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <input
                  id="name"
                  name="name"
                  data-testid="waitlist-name-input"
                  type="text"
                  placeholder={t('joinNamePlaceholder')}
                  value={formData.name}
                  onChange={handleChange}
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="sr-only">Email</label>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700/50 focus-within:border-cyan-400/50 transition-colors">
                <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <input
                  id="email"
                  name="email"
                  data-testid="waitlist-email-input"
                  type="email"
                  placeholder={t('joinEmailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange}
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="sr-only">Phone</label>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700/50 focus-within:border-cyan-400/50 transition-colors">
                <Phone className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <input
                  id="phone"
                  name="phone"
                  data-testid="waitlist-phone-input"
                  type="tel"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Note */}
            <div>
              <label htmlFor="note" className="sr-only">Note</label>
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700/50 focus-within:border-cyan-400/50 transition-colors">
                <MessageSquare className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <input
                  id="note"
                  name="note"
                  data-testid="waitlist-note-input"
                  type="text"
                  placeholder={t('joinNotePlaceholder')}
                  value={formData.note}
                  onChange={handleChange}
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Error */}
            {error && (
              <div data-testid="waitlist-error" className="flex items-center gap-2 text-xs text-rose-400">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Success */}
            {isSuccess && (
              <div data-testid="waitlist-success" className="text-sm text-emerald-400 font-medium">
                {t('joinSuccessMsg')}
              </div>
            )}

            {/* Buttons */}
            <div className="flex items-center gap-3">
              <button
                type="submit"
                data-testid="waitlist-submit-btn"
                disabled={isSubmitting || isSuccess}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-medium text-slate-900 hover:bg-emerald-400 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-200"
              >
                {isSubmitting ? t('joinSubmittingBtn') : isSuccess ? t('joinJoinedBtn') : t('joinSubmitBtn')}
              </button>
              <button
                type="button"
                data-testid="waitlist-clear-btn"
                onClick={handleClear}
                disabled={isSubmitting}
                className="px-4 py-3 rounded-xl text-sm bg-slate-800/60 text-slate-300 hover:bg-slate-800 transition-colors"
              >
                {t('joinClearBtn')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
